<template>
    <div class="waba-activity-card" :class="{ expanded: isExpanded }">
        <div class="card-header" @click="toggleExpand">
            <div class="left-section">
                <div class="activity-icon" :class="getActivityClass()">
                    <span class="material-icons">{{ getActivityIcon() }}</span>
                </div>
                <div class="activity-info">
                    <div class="activity-title">{{ getActivityTitle() }}</div>
                    <div class="activity-subtitle">{{ getActivitySubtitle() }}</div>
                </div>
            </div>
            <div class="right-section">
                <div class="activity-time" v-tippy="{ content: $moment(activity.createdAt).format('DD/MM/YYYY HH:mm:ss') }">
                    {{ $moment(activity.createdAt).fromNow() }}
                </div>
                <div class="expand-icon">
                    <span class="material-icons">{{ isExpanded ? 'expand_less' : 'expand_more' }}</span>
                </div>
            </div>
        </div>
        
        <div class="card-body" v-if="isExpanded">
            <div class="details-grid">
                <div class="detail-item" v-if="$_.get(activity, 'payload.event')">
                    <span class="detail-label">Event</span>
                    <span class="detail-value event-badge" :class="getEventBadgeClass()">
                        {{ $_.get(activity, 'payload.event') }}
                    </span>
                </div>
                <div class="detail-item" v-if="$_.get(activity, 'payload.originalEvent')">
                    <span class="detail-label">Original Event</span>
                    <span class="detail-value event-badge" :class="getOriginalEventBadgeClass()">
                        {{ $_.get(activity, 'payload.originalEvent') }}
                    </span>
                </div>
                <div class="detail-item" v-if="getViolationType()">
                    <span class="detail-label">Violation Type</span>
                    <span class="detail-value violation-badge">
                        {{ getViolationType() }}
                    </span>
                </div>
                <div class="detail-item" v-if="$_.get(activity, 'payload.channelId')">
                    <span class="detail-label">Channel ID</span>
                    <span class="detail-value mono">{{ $_.get(activity, 'payload.channelId') }}</span>
                </div>
                <div class="detail-item" v-if="$_.get(activity, 'payload.message_template_name')">
                    <span class="detail-label">Template Name</span>
                    <span class="detail-value">{{ $_.get(activity, 'payload.message_template_name') }}</span>
                </div>
                <div class="detail-item" v-if="$_.get(activity, 'payload.message_template_id')">
                    <span class="detail-label">Template ID</span>
                    <span class="detail-value mono">{{ $_.get(activity, 'payload.message_template_id') }}</span>
                </div>
                <div class="detail-item" v-if="$_.get(activity, 'payload.message_template_language')">
                    <span class="detail-label">Language</span>
                    <span class="detail-value">{{ $_.get(activity, 'payload.message_template_language') }}</span>
                </div>
                <div class="detail-item" v-if="$_.get(activity, 'payload.message_template_category')">
                    <span class="detail-label">Category</span>
                    <span class="detail-value category-badge">{{ $_.get(activity, 'payload.message_template_category') }}</span>
                </div>
                <div class="detail-item" v-if="$_.get(activity, 'payload.reason') && $_.get(activity, 'payload.reason') !== 'NONE'">
                    <span class="detail-label">Reason</span>
                    <span class="detail-value reason-badge">{{ $_.get(activity, 'payload.reason') }}</span>
                </div>
                <div class="detail-item" v-if="$_.get(activity, 'payload.previous_category')">
                    <span class="detail-label">Previous Category</span>
                    <span class="detail-value">{{ $_.get(activity, 'payload.previous_category') }}</span>
                </div>
                <div class="detail-item" v-if="$_.get(activity, 'payload.new_category')">
                    <span class="detail-label">New Category</span>
                    <span class="detail-value">{{ $_.get(activity, 'payload.new_category') }}</span>
                </div>
                <div class="detail-item" v-if="$_.get(activity, 'payload.correct_category')">
                    <span class="detail-label">Correct Category</span>
                    <span class="detail-value">{{ $_.get(activity, 'payload.correct_category') }}</span>
                </div>
                <div class="detail-item" v-if="$_.get(activity, 'payload.previous_quality_score')">
                    <span class="detail-label">Previous Quality</span>
                    <span class="detail-value quality-badge" :class="getQualityClass($_.get(activity, 'payload.previous_quality_score'))">
                        {{ $_.get(activity, 'payload.previous_quality_score') }}
                    </span>
                </div>
                <div class="detail-item" v-if="$_.get(activity, 'payload.new_quality_score')">
                    <span class="detail-label">New Quality</span>
                    <span class="detail-value quality-badge" :class="getQualityClass($_.get(activity, 'payload.new_quality_score'))">
                        {{ $_.get(activity, 'payload.new_quality_score') }}
                    </span>
                </div>
            </div>
            
            <!-- Violation Type Description -->
            <div class="violation-description" v-if="violationDescription">
                <div class="violation-header">
                    <span class="material-icons">gavel</span>
                    <span>Violation Policy Details</span>
                </div>
                <div class="violation-content">
                    <div class="violation-title">{{ getViolationType() }}</div>
                    <p class="violation-policy">{{ violationDescription.policy }}</p>
                    <div class="violation-examples" v-if="violationDescription.allowed && violationDescription.allowed.length > 0">
                        <div class="examples-title">
                            <span class="material-icons">check_circle</span>
                            Allowed Examples:
                        </div>
                        <ul>
                            <li v-for="(example, idx) in violationDescription.allowed" :key="'allowed-' + idx">{{ example }}</li>
                        </ul>
                    </div>
                    <div class="violation-examples not-allowed" v-if="violationDescription.notAllowed && violationDescription.notAllowed.length > 0">
                        <div class="examples-title">
                            <span class="material-icons">cancel</span>
                            Not Allowed Examples:
                        </div>
                        <ul>
                            <li v-for="(example, idx) in violationDescription.notAllowed" :key="'notallowed-' + idx">{{ example }}</li>
                        </ul>
                    </div>
                </div>
            </div>
            
            <div class="json-preview">
                <div class="json-header">
                    <span class="material-icons">data_object</span>
                    <span>Raw Payload</span>
                    <button class="copy-btn" @click.stop="copyPayload" v-tippy="{ content: 'Kopyala' }">
                        <span class="material-icons">{{ copied ? 'check' : 'content_copy' }}</span>
                    </button>
                </div>
                <pre class="json-content">{{ prettyPayload }}</pre>
            </div>
        </div>
    </div>
</template>

<script>
import Vue from "vue";
import _ from "lodash";

const ACTIVITY_TYPE_CONFIG = {
    waba_supsis_partner_remove: { icon: "person_remove", title: "Partner Removed", class: "danger" },
    waba_supsis_partner_added: { icon: "person_add", title: "Partner Added", class: "success" },
    waba_supsis_account_deleted: { icon: "delete_forever", title: "Account Deleted", class: "danger" },
    waba_supsis_account_violation: { icon: "warning", title: "Account Violation", class: "warning" },
    waba_supsis_ad_account_linked: { icon: "link", title: "Ad Account Linked", class: "info" },
    waba_supsis_auth_intl_price_eligibility_update: { icon: "paid", title: "Price Eligibility Update", class: "info" },
    waba_supsis_disabled_update: { icon: "block", title: "Disabled Update", class: "warning" },
    waba_supsis_mm_lite_terms_signed: { icon: "description", title: "MM Lite Terms Signed", class: "success" },
    waba_supsis_partner_app_installed: { icon: "install_mobile", title: "Partner App Installed", class: "success" },
    waba_supsis_partner_app_uninstalled: { icon: "delete", title: "Partner App Uninstalled", class: "warning" },
    waba_supsis_partner_client_certification_status_update: { icon: "verified", title: "Certification Status Update", class: "info" },
    waba_supsis_business_primary_location_country_update: { icon: "location_on", title: "Location Country Update", class: "info" },
    waba_supsis_volume_based_pricing_tier_update: { icon: "trending_up", title: "Pricing Tier Update", class: "info" },
    waba_supsis_message_template_components_update: { icon: "article", title: "Template Components Update", class: "info" },
    waba_supsis_message_template_status_update: { icon: "speed", title: "Template Status Update", class: "info" },
    waba_supsis_template_category_update: { icon: "category", title: "Template Category Update", class: "info" },
};

const VIOLATION_TYPE_DESCRIPTIONS = {
    ADULT: {
        policy: "Businesses may not transact in the sale or use of adult products or services.",
        allowed: [
            "Products promoting family planning and contraception, which focus on the contraceptive features of the product, and not on sexual pleasure or sexual enhancement"
        ],
        notAllowed: [
            "Sex toys",
            "Videos or live shows for adult entertainment",
            "Sexual enhancement products",
            "Sexually suggestive services"
        ]
    },
    ALCOHOL: {
        policy: "Businesses may not transact in the sale of alcohol.",
        allowed: [
            "Books or DVDs about alcohol",
            "Alcohol-related items, including glasses, coolers, and wine bottle holders"
        ],
        notAllowed: [
            "Alcoholic beverages",
            "Kits for producing alcohol"
        ]
    },
    ANIMALS: {
        policy: "Businesses may not transact in the sale of any animals.",
        allowed: [
            "Animal cages",
            "Products for animals (toys, collars, etc.)",
            "Veterinary services",
            "Grooming services",
            "Boarding services",
            "Pet adoption services"
        ],
        notAllowed: [
            "Live animals",
            "Livestock",
            "Prohibited animal parts, including but not limited to bone, teeth, horn, ivory, taxidermy, organs, external limbs, secretions, or carcasses",
            "Any product or part, including but not limited to leather, skin, hide, fur, wool, or hair from any dogs, cats, and endangered or threatened animals"
        ]
    },
    BODY_PARTS_FLUIDS: {
        policy: "Businesses may not transact in the sale of human body parts or fluids.",
        allowed: [
            "Blood donation centers",
            "Hair extensions and wigs"
        ],
        notAllowed: [
            "Blood",
            "Urine",
            "Body parts",
            "Organs",
            "Human tissue",
            "Teeth"
        ]
    },
    DATING: {
        policy: "Businesses may not transact in or facilitate online dating services.",
        allowed: [],
        notAllowed: []
    },
    DIGITAL_SERVICES_PRODUCTS: {
        policy: "Businesses may not transact in the sale, including links to or processing of any subscription sales, renewals, or upgrades, etc., of digital content, digital subscriptions, or digital accounts.",
        allowed: [
            "Authentic audio or video CDs, DVDs, and Blu Ray",
            "Digital devices, including Smartphones, video game consoles, and TVs"
        ],
        notAllowed: [
            "Downloadable content, including PDFs, music, games, movies, etc.",
            "Digital accounts, including games accounts",
            "Digital subscriptions and internet streaming services, including TV, Mobile, etc.",
            "Digital coupons"
        ]
    },
    DRUGS: {
        policy: "Businesses may not transact in the sale of illegal, prescription, or recreational drugs. Manufacturers and healthcare services who do not engage in direct sale are allowed.",
        allowed: [],
        notAllowed: [
            "Drugs, including marijuana and marijuana products",
            "Drug paraphernalia, including pipes and bongs",
            "Prescription drugs"
        ]
    },
    GAMBLING: {
        policy: "Businesses may not transact in or facilitate gambling, games of skill, or lotteries, including online casinos, sports books, bingo, or poker if it costs money.",
        allowed: [],
        notAllowed: []
    },
    HEALTHCARE: {
        policy: "Businesses may not transact in certain healthcare products, including medical devices, and smoking cessation products containing nicotine. Manufacturers and healthcare services who do not engage in direct sale are allowed.",
        allowed: [
            "Lifestyle and fitness accessories",
            "Contact lenses, bandages and braces for physical injuries",
            "Thermometers",
            "Testing kits for medical conditions or diseases",
            "Breast pumps",
            "First-aid kits"
        ],
        notAllowed: [
            "Medical Devices",
            "Smoking Cessation Products",
            "Nicotine patches",
            "Nicotine gum"
        ]
    },
    ILLEGAL_PRODUCTS: {
        policy: "Businesses may not transact in the operation or exchange of illegal products or services.",
        allowed: [],
        notAllowed: []
    },
    MISLEADING: {
        policy: "WhatsApp prohibits business models, goods, items, or services that we determine may be or are fraudulent, misleading, offensive, or deceptive, or that may be or are exploitative, inappropriate, or exert undue pressure on targeted groups.",
        allowed: [],
        notAllowed: [
            "Multilevel marketing",
            "Penny auctions",
            "ICOs and binary options",
            "Payday loans, paycheck advances, P2P lending, debt collection, and bail bonds",
            "Diet, weight loss, or other health related products that imply or attempt to generate negative self-perception"
        ]
    },
    OVERTLY_SEXUALIZED_POSITIONING: {
        policy: "Businesses may not position products or services in a sexually suggestive manner.",
        allowed: [],
        notAllowed: [
            "Implied nudity",
            "Implied sexual acts",
            "Zoomed-in sexual images"
        ]
    },
    REAL_FAKE_CURRENCY: {
        policy: "Businesses may not transact in the sale of real, virtual, or fake currency.",
        allowed: [],
        notAllowed: [
            "Real money (cash or cash equivalent instruments and coins)",
            "Replica or prop money",
            "Digital or cryptocurrency",
            "Active bank credit or debit cards",
            "Store credit cards or coupons",
            "Pre-paid credit or debit cards",
            "Checks or checkbooks",
            "Equipment to create counterfeit currency or financial instruments"
        ]
    },
    SCAM: {
        policy: "Businesses may not transact in any activities that promote or facilitate scams.",
        allowed: [],
        notAllowed: []
    },
    SUPPLEMENTS: {
        policy: "Businesses may not transact in the sale of unsafe ingestible supplements, as determined by WhatsApp in its sole discretion.",
        allowed: [],
        notAllowed: [
            "Anabolic steroids",
            "Chitosan",
            "Comfrey",
            "Dehydroepiandrosterone",
            "Ephedra",
            "Human growth hormones"
        ]
    },
    THIRD_PARTY_INFRINGEMENTS: {
        policy: "Transactions may not contain content that infringes upon or violates the intellectual property rights of any third party, including copyright or trademark. This includes, but is not limited to, the sale of counterfeit products, such as goods that copy the trademark (name or logo) and/or distinctive features of another company's products to imitate a genuine product.",
        allowed: [],
        notAllowed: [
            "Counterfeits, knockoffs, or replicas of branded goods, or posts offering goods that are likely to confuse consumers about the source, sponsorship or affiliation of those goods",
            "Unauthorized or pirated copies of copyrighted works, such as videos, movies, TV shows and broadcasts, video games, CDs or other musical works, books, etc."
        ]
    },
    TOBACCO: {
        policy: "Businesses may not transact in the sale of tobacco products or tobacco paraphernalia.",
        allowed: [
            "Apparel featuring a tobacco brand logo"
        ],
        notAllowed: [
            "Cigarettes, cigars, and chewing tobacco",
            "Tobacco pipes and paraphernalia",
            "Tobacco rolling machines",
            "Hookahs",
            "Bongs",
            "Rolling papers",
            "Electronic cigarettes (E-cigarettes) or tobacco devices"
        ]
    },
    UNAUTHORIZED_MEDIA: {
        policy: "Businesses may not transact in the sale of devices that facilitate or encourage streaming digital content in an unauthorized manner or interfering with the functionality of electronic devices.",
        allowed: [
            "Add-on equipment for streaming devices such as keyboards and remotes"
        ],
        notAllowed: [
            "Sale of streaming devices loaded with software that facilitates unauthorized access to content",
            "Jailbroken or loaded devices",
            "Jamming or descrambling devices",
            "Wiretapping devices"
        ]
    },
    WEAPONS: {
        policy: "Businesses may not transact in the sale or use of weapons, ammunition, or explosives.",
        allowed: [
            "Promoting safety training or licenses for legal weapons"
        ],
        notAllowed: [
            "Firearms and firearm parts",
            "Paintball guns",
            "BB Guns",
            "Fireworks",
            "Pepper spray",
            "Tasers",
            "Gun ranges",
            "Gun shows"
        ]
    }
};

export default Vue.extend({
    name: "WabaActivityCard",
    props: {
        activity: {
            type: Object,
            default: () => ({}),
        },
    },
    data() {
        return {
            isExpanded: false,
            copied: false,
        };
    },
    computed: {
        prettyPayload() {
            try {
                return JSON.stringify(this.$_.get(this.activity, "payload", {}), null, 2);
            } catch (e) {
                return String(this.$_.get(this.activity, "payload", ""));
            }
        },
        violationDescription() {
            const violationType = this.getViolationType();
            if (!violationType) return null;
            return VIOLATION_TYPE_DESCRIPTIONS[violationType] || null;
        },
    },
    methods: {
        toggleExpand() {
            this.isExpanded = !this.isExpanded;
        },
        getActivityConfig() {
            return ACTIVITY_TYPE_CONFIG[this.activity.type] || { icon: "info", title: "Unknown Activity", class: "default" };
        },
        getActivityIcon() {
            return this.getActivityConfig().icon;
        },
        getActivityTitle() {
            return this.getActivityConfig().title;
        },
        getActivityClass() {
            return this.getActivityConfig().class;
        },
        getViolationType() {
            // violation_type can be in payload.violation_type or payload.violation_info.violation_type
            return this.$_.get(this.activity, "payload.violation_info.violation_type") || 
                   this.$_.get(this.activity, "payload.violation_type") || 
                   null;
        },
        getActivitySubtitle() {
            const payload = this.activity.payload || {};
            const parts = [];
            
            const violationType = this.getViolationType();
            if (violationType) {
                parts.push(`Violation: ${violationType}`);
            }
            if (payload.message_template_name) {
                parts.push(`Template: ${payload.message_template_name}`);
            }
            if (payload.originalEvent) {
                parts.push(payload.originalEvent);
            }
            if (payload.event && payload.event !== "MESSAGE_TEMPLATE_STATUS_UPDATE" && payload.event !== "MESSAGE_TEMPLATE_COMPONENTS_UPDATE" && payload.event !== "MESSAGE_TEMPLATE_CATEGORY_UPDATE" && payload.event !== "ACCOUNT_VIOLATION") {
                parts.push(payload.event);
            }
            
            return parts.length > 0 ? parts.join(" • ") : this.activity.type;
        },
        getEventBadgeClass() {
            const event = this.$_.get(this.activity, "payload.event", "");
            if (event.includes("APPROVED") || event.includes("SUCCESS") || event.includes("ADDED")) return "success";
            if (event.includes("REJECTED") || event.includes("DELETED") || event.includes("REMOVED") || event.includes("VIOLATION")) return "danger";
            if (event.includes("PENDING") || event.includes("WARNING") || event.includes("DISABLED")) return "warning";
            return "info";
        },
        getOriginalEventBadgeClass() {
            const event = this.$_.get(this.activity, "payload.originalEvent", "");
            if (event === "APPROVED" || event === "REINSTATED") return "success";
            if (event === "REJECTED" || event === "PAUSED" || event === "DISABLED" || event === "DELETED" || event === "PENDING_DELETION") return "danger";
            if (event === "PENDING" || event === "IN_APPEAL" || event === "FLAGGED") return "warning";
            return "info";
        },
        getQualityClass(quality) {
            if (quality === "GREEN") return "green";
            if (quality === "YELLOW") return "yellow";
            if (quality === "RED") return "red";
            return "";
        },
        async copyPayload() {
            try {
                await navigator.clipboard.writeText(this.prettyPayload);
                this.copied = true;
                setTimeout(() => {
                    this.copied = false;
                }, 2000);
            } catch (err) {
                console.error("Failed to copy:", err);
            }
        },
    },
});
</script>

<style scoped lang="scss">
.waba-activity-card {
    background: var(--color-bg-default);
    border: 1px solid var(--color-border-default);
    border-radius: 7px;
    margin-bottom: 9px;
    overflow: hidden;
    transition: all 0.2s ease;

    &:hover {
        border-color: var(--color-border-strong);
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
    }

    &.expanded {
        border-color: var(--color-border-strong);
    }

    .card-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 12px;
        cursor: pointer;
        user-select: none;

        .left-section {
            display: flex;
            align-items: center;
            gap: 9px;
            flex: 1;
            min-width: 0;

            .activity-icon {
                width: 31px;
                height: 31px;
                border-radius: 8px;
                display: flex;
                align-items: center;
                justify-content: center;
                flex-shrink: 0;

                .material-icons {
                    font-size: 17px;
                    color: white;
                }

                &.success { background: linear-gradient(135deg, #10b981, #059669); }
                &.danger { background: linear-gradient(135deg, #ef4444, #dc2626); }
                &.warning { background: linear-gradient(135deg, #f59e0b, #d97706); }
                &.info { background: linear-gradient(135deg, #3b82f6, #2563eb); }
                &.default { background: linear-gradient(135deg, #6b7280, #4b5563); }
            }

            .activity-info {
                min-width: 0;
                flex: 1;

                .activity-title {
                    font-weight: 600;
                    font-size: 12px;
                    color: var(--color-text-default);
                    margin-bottom: 1px;
                }

                .activity-subtitle {
                    font-size: 10px;
                    color: var(--color-text-secondary);
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                }
            }
        }

        .right-section {
            display: flex;
            align-items: center;
            gap: 7px;
            flex-shrink: 0;

            .activity-time {
                font-size: 10px;
                color: var(--color-text-secondary);
                white-space: nowrap;
            }

            .expand-icon {
                .material-icons {
                    font-size: 17px;
                    color: var(--color-icon-default);
                    transition: transform 0.2s ease;
                }
            }
        }
    }

    .card-body {
        padding: 0 12px 12px;
        border-top: 1px solid var(--color-border-default);
        margin-top: 0;
        padding-top: 12px;

        .details-grid {
            display: grid;
            grid-template-columns: repeat(auto-fill, minmax(155px, 1fr));
            gap: 9px;
            margin-bottom: 12px;

            .detail-item {
                display: flex;
                flex-direction: column;
                gap: 3px;

                .detail-label {
                    font-size: 10px;
                    font-weight: 600;
                    text-transform: uppercase;
                    color: var(--color-text-secondary);
                    letter-spacing: 0.5px;
                }

                .detail-value {
                    font-size: 11px;
                    color: var(--color-text-default);
                    word-break: break-all;

                    &.mono {
                        font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
                        font-size: 10px;
                        background: var(--color-bg-neutral-subtle);
                        padding: 3px 7px;
                        border-radius: 4px;
                    }

                    &.event-badge, &.category-badge, &.reason-badge, &.quality-badge, &.violation-badge {
                        display: inline-block;
                        padding: 2px 7px;
                        border-radius: 4px;
                        font-size: 10px;
                        font-weight: 600;
                    }

                    &.event-badge {
                        &.success { background: #dcfce7; color: #166534; }
                        &.danger { background: #fee2e2; color: #991b1b; }
                        &.warning { background: #fef3c7; color: #92400e; }
                        &.info { background: #dbeafe; color: #1e40af; }
                    }

                    &.category-badge {
                        background: #e0e7ff;
                        color: #3730a3;
                    }

                    &.reason-badge {
                        background: #fef3c7;
                        color: #92400e;
                    }

                    &.violation-badge {
                        background: #fee2e2;
                        color: #991b1b;
                    }

                    &.quality-badge {
                        &.green { background: #dcfce7; color: #166534; }
                        &.yellow { background: #fef3c7; color: #92400e; }
                        &.red { background: #fee2e2; color: #991b1b; }
                    }
                }
            }
        }

        .violation-description {
            background: #fef2f2;
            border: 1px solid #fecaca;
            border-radius: 7px;
            margin-bottom: 12px;
            overflow: hidden;

            .violation-header {
                display: flex;
                align-items: center;
                gap: 7px;
                padding: 9px 11px;
                background: #fee2e2;
                font-size: 11px;
                font-weight: 600;
                color: #991b1b;

                .material-icons {
                    font-size: 15px;
                }
            }

            .violation-content {
                padding: 11px;

                .violation-title {
                    font-size: 12px;
                    font-weight: 700;
                    color: #991b1b;
                    margin-bottom: 7px;
                }

                .violation-policy {
                    font-size: 11px;
                    line-height: 1.6;
                    color: #7f1d1d;
                    margin: 0 0 9px 0;
                }

                .violation-examples {
                    margin-top: 9px;

                    .examples-title {
                        display: flex;
                        align-items: center;
                        gap: 4px;
                        font-size: 10px;
                        font-weight: 600;
                        color: #166534;
                        margin-bottom: 4px;

                        .material-icons {
                            font-size: 13px;
                        }
                    }

                    &.not-allowed {
                        .examples-title {
                            color: #991b1b;
                        }
                    }

                    ul {
                        margin: 0;
                        padding-left: 17px;

                        li {
                            font-size: 10px;
                            line-height: 1.5;
                            color: #374151;
                            margin-bottom: 3px;

                            &:last-child {
                                margin-bottom: 0;
                            }
                        }
                    }
                }
            }
        }

        .json-preview {
            background: var(--color-bg-neutral-subtle);
            border-radius: 7px;
            overflow: hidden;

            .json-header {
                display: flex;
                align-items: center;
                gap: 7px;
                padding: 8px 11px;
                background: var(--color-bg-neutral-default);
                font-size: 10px;
                font-weight: 600;
                color: var(--color-text-secondary);

                .material-icons {
                    font-size: 13px;
                }

                .copy-btn {
                    margin-left: auto;
                    background: none;
                    border: none;
                    cursor: pointer;
                    padding: 3px;
                    border-radius: 4px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    color: var(--color-text-secondary);
                    transition: all 0.2s ease;

                    &:hover {
                        background: var(--color-bg-default);
                        color: var(--color-text-default);
                    }

                    .material-icons {
                        font-size: 13px;
                    }
                }
            }

            .json-content {
                margin: 0;
                padding: 11px;
                font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
                font-size: 10px;
                line-height: 1.5;
                color: var(--color-text-default);
                overflow-x: auto;
                max-height: 230px;
                white-space: pre-wrap;
                word-break: break-all;
            }
        }
    }
}
</style>
